import React, { useState, useEffect } from 'react';
import {
  Typography,
  Button,
  Paper,
  IconButton,
  TextField,
  Box,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  InputAdornment,
  Card,
  CardContent,
  Grid,
  useTheme,
  useMediaQuery,
  Skeleton,
  Alert,
  Tooltip,
  Fab,
  Avatar,
  Badge,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Divider
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Search as SearchIcon,
  Person as PersonIcon,
  Work as WorkIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Badge as BadgeIcon,
  LocationOn as LocationIcon,
  Star as StarIcon,
  Group as GroupIcon,
  CalendarToday as CalendarIcon,
  Assignment as AssignmentIcon,
  Send as SendIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import config from '../config';

// Componente de detalle de trabajador
const DetalleTrabjadorDialog = ({ open, onClose, trabajador }) => {
  if (!trabajador) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar 
            sx={{ 
              bgcolor: '#1A237E', 
              width: 56, 
              height: 56,
              fontSize: '1.5rem'
            }}
          >
            {trabajador.nombre_trabajador?.[0]}{trabajador.apellido_trabajador?.[0]}
          </Avatar>
          <Box>
            <Typography variant="h6">
              {trabajador.nombre_trabajador} {trabajador.apellido_trabajador}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {trabajador.profesion || 'Trabajador'}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2, borderRadius: 2 }}>
              <Typography variant="h6" gutterBottom sx={{ color: '#1A237E', fontWeight: 'bold' }}>
                <PersonIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Información Personal
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemAvatar>
                    <BadgeIcon color="primary" />
                  </ListItemAvatar>
                  <ListItemText 
                    primary="Documento" 
                    secondary={`${trabajador.tipo_documento || '-'}: ${trabajador.numero_documento || '-'}`} 
                  />
                </ListItem>
                <ListItem>
                  <ListItemAvatar>
                    <EmailIcon color="primary" />
                  </ListItemAvatar>
                  <ListItemText 
                    primary="Email" 
                    secondary={trabajador.email_trabajador || '-'} 
                  />
                </ListItem>
                <ListItem>
                  <ListItemAvatar>
                    <PhoneIcon color="primary" />
                  </ListItemAvatar>
                  <ListItemText 
                    primary="Teléfono" 
                    secondary={trabajador.telefono_trabajador || '-'} 
                  />
                </ListItem>
              </List>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2, borderRadius: 2 }}>
              <Typography variant="h6" gutterBottom sx={{ color: '#1A237E', fontWeight: 'bold' }}>
                <WorkIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Información Laboral
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemAvatar>
                    <StarIcon color="warning" />
                  </ListItemAvatar>
                  <ListItemText 
                    primary="Escala" 
                    secondary={trabajador.descripcion_escala || '-'} 
                  />
                </ListItem>
                <ListItem>
                  <ListItemAvatar>
                    <AssignmentIcon color="success" />
                  </ListItemAvatar>
                  <ListItemText 
                    primary="Nivel" 
                    secondary={trabajador.det_nivel_usuario || '-'} 
                  />
                </ListItem>
                <ListItem>
                  <ListItemAvatar>
                    <CalendarIcon color="info" />
                  </ListItemAvatar>
                  <ListItemText 
                    primary="Fecha Contratación" 
                    secondary={trabajador.fecha_contratacion ? new Date(trabajador.fecha_contratacion).toLocaleDateString() : '-'} 
                  />
                </ListItem>
              </List>
            </Paper>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained">Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
};

// Componente de tarjeta para vista móvil
const TrabajadorCard = ({ trabajador, onView, onEdit, onDelete, onResend }) => {
  const getInitials = (nombre, apellido) => {
    return `${nombre?.[0] || ''}${apellido?.[0] || ''}`.toUpperCase();
  };

  const getNivelColor = (nivel) => {
    switch(nivel?.toLowerCase()) {
      case 'administrador': return '#f44336';
      case 'supervisor': return '#ff9800';
      case 'operario': return '#4caf50';
      case 'trabajador': return '#2196f3';
      default: return '#757575';
    }
  };

  return (
    <Card sx={{ 
      mb: 2,
      borderRadius: 3,
      border: '1px solid rgba(26, 35, 126, 0.1)',
      transition: 'all 0.3s ease',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: '0 8px 25px rgba(26, 35, 126, 0.15)'
      }
    }}>
      <CardContent sx={{ p: 3 }}>
        {/* Header con avatar y nombre */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Badge
            overlap="circular"
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            badgeContent={
              <Box sx={{
                width: 16,
                height: 16,
                borderRadius: '50%',
                bgcolor: '#4caf50',
                border: '2px solid white'
              }} />
            }
          >
            <Avatar
              sx={{
                width: 56,
                height: 56,
                background: 'linear-gradient(135deg, #4FC3F7 0%, #29B6F6 100%)',
                color: 'white',
                fontWeight: 'bold',
                fontSize: '1.2rem',
                mr: 2
              }}
            >
              {getInitials(trabajador.nombre_trabajador, trabajador.apellido_trabajador)}
            </Avatar>
          </Badge>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1A237E', mb: 0.5 }}>
              {trabajador.nombre_trabajador} {trabajador.apellido_trabajador}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              ID: {trabajador.id_trabajador}
            </Typography>
          </Box>
        </Box>

        {/* Información principal */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <BadgeIcon sx={{ color: '#4FC3F7', fontSize: '1rem' }} />
              <Typography variant="body2" color="text.secondary">
                {trabajador.tipo_documento}: {trabajador.numero_documento}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <EmailIcon sx={{ color: '#4FC3F7', fontSize: '1rem' }} />
              <Typography variant="body2" color="text.secondary">
                {trabajador.email_trabajador}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <PhoneIcon sx={{ color: '#4FC3F7', fontSize: '1rem' }} />
              <Typography variant="body2" color="text.secondary">
                {trabajador.telefono_trabajador}
              </Typography>
            </Box>
          </Grid>
        </Grid>

        {/* Chips de información laboral */}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
          <Chip 
            label={trabajador.descripcion_escala || 'Sin escala'}
            size="small"
            sx={{ 
              bgcolor: 'rgba(255, 152, 0, 0.1)',
              color: '#FF9800',
              fontWeight: 'bold'
            }}
          />
          <Chip 
            label={trabajador.det_nivel_usuario || 'Sin nivel'}
            size="small"
            sx={{ 
              bgcolor: `rgba(${getNivelColor(trabajador.det_nivel_usuario)?.slice(1).match(/.{2}/g)?.map(hex => parseInt(hex, 16)).join(', ')}, 0.1)`,
              color: getNivelColor(trabajador.det_nivel_usuario),
              fontWeight: 'bold'
            }}
          />
        </Box>

        {/* Acciones */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Tooltip title="Ver detalles">
            <IconButton
              size="small"
              onClick={() => onView(trabajador)}
              sx={{ 
                bgcolor: 'info.light', 
                color: 'white',
                '&:hover': { bgcolor: 'info.main' }
              }}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Editar trabajador">
            <IconButton
              size="small"
              onClick={() => onEdit(trabajador)}
              sx={{ 
                bgcolor: 'primary.light', 
                color: 'white',
                '&:hover': { bgcolor: 'primary.main' }
              }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Enviar onboarding APP">
            <IconButton size="small" onClick={() => onResend(trabajador)}
              disabled={!trabajador.email_trabajador}
              sx={{ bgcolor: '#25D366', color: 'white', '&:hover': { bgcolor: '#128C7E' }, '&.Mui-disabled': { bgcolor: '#E2E8F0' } }}>
              <SendIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Eliminar trabajador">
            <IconButton
              size="small"
              onClick={() => onDelete(trabajador)}
              sx={{
                bgcolor: 'error.light',
                color: 'white',
                '&:hover': { bgcolor: 'error.main' }
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </CardContent>
    </Card>
  );
};

function TrabajadoresAdmin() {
  const [trabajadores, setTrabajadores] = useState([]);
  const [filteredTrabajadores, setFilteredTrabajadores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(20);
  const [orderBy, setOrderBy] = useState('nombre_trabajador');
  const [order, setOrder] = useState('asc');
  const [error, setError] = useState(null);
  const [selectedTrabajador, setSelectedTrabajador] = useState(null);
  const [openDetail, setOpenDetail] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, msg: '', severity: 'success' });

  const { user } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    fetchTrabajadores();
  }, []);

  useEffect(() => {
    const filterAndSortTrabajadores = () => {
      let filtered = [...trabajadores];
      
      if (searchTerm) {
        filtered = filtered.filter(trabajador =>
          Object.values(trabajador)
            .join(' ')
            .toLowerCase()
            .includes(searchTerm.toLowerCase())
        );
      }

      filtered.sort((a, b) => {
        const isAsc = order === 'asc';
        if (a[orderBy] < b[orderBy]) return isAsc ? -1 : 1;
        if (a[orderBy] > b[orderBy]) return isAsc ? 1 : -1;
        return 0;
      });

      setFilteredTrabajadores(filtered);
      setPage(0);
    };

    filterAndSortTrabajadores();
  }, [trabajadores, searchTerm, order, orderBy]);

  const fetchTrabajadores = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${config.API_BASE_URL}/api/trabajadores`, {
        headers: { Authorization: `Bearer ${user.token}` }
      });
      setTrabajadores(response.data);
      setError(null);
    } catch (error) {
      console.error('Error al obtener los trabajadores:', error);
      setError('Error al cargar los trabajadores. Por favor, inténtelo de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (trabajador) => {
    if (window.confirm(`¿Estás seguro de eliminar a ${trabajador.nombre_trabajador} ${trabajador.apellido_trabajador}?`)) {
      try {
        await axios.delete(`${config.API_BASE_URL}/api/trabajadores/${trabajador.id_trabajador}`, {
          headers: { Authorization: `Bearer ${user.token}` }
        });
        fetchTrabajadores();
      } catch (error) {
        console.error('Error al eliminar el trabajador:', error);
        setError('Error al eliminar el trabajador.');
      }
    }
  };

  const handleView = (trabajador) => {
    setSelectedTrabajador(trabajador);
    setOpenDetail(true);
  };

  const handleEdit = (trabajador) => {
    navigate(`/home/trabajadores/editar/${trabajador.id_trabajador}`);
  };

  const handleReenviarBienvenida = async (trabajador) => {
    try {
      const res = await axios.post(
        `${config.API_BASE_URL}/api/trabajadores/${trabajador.id_trabajador}/reenviar-bienvenida`,
        {},
        { headers: { Authorization: `Bearer ${user.token}` } }
      );
      setSnackbar({ open: true, msg: res.data.mensaje, severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, msg: err.response?.data?.error || 'Error al enviar', severity: 'error' });
    }
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const createSortHandler = (property) => () => {
    handleRequestSort(property);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  };

  const getInitials = (nombre, apellido) => {
    return `${nombre?.[0] || ''}${apellido?.[0] || ''}`.toUpperCase();
  };

  // Estadísticas
  const estadisticas = {
    total: trabajadores.length,
    activos: trabajadores.filter(t => t.is_active !== false).length,
    supervisores: trabajadores.filter(t => t.det_nivel_usuario?.toLowerCase().includes('supervisor')).length,
    operarios: trabajadores.filter(t => t.det_nivel_usuario?.toLowerCase().includes('operario')).length
  };

  if (loading && trabajadores.length === 0) {
    return (
      <Box sx={{ width: '100%', p: { xs: 2, sm: 3, md: 4 } }}>
        <Skeleton variant="text" width="60%" height={60} sx={{ mb: 3 }} />
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[1, 2, 3, 4].map((item) => (
            <Grid item xs={6} sm={3} key={item}>
              <Skeleton variant="rectangular" height={100} sx={{ borderRadius: 2 }} />
            </Grid>
          ))}
        </Grid>
        <Skeleton variant="rectangular" width="100%" height={400} sx={{ borderRadius: 3 }} />
      </Box>
    );
  }

  return (
    <Box sx={{ 
      width: '100%',
      minHeight: '100%',
      p: { xs: 2, sm: 3, md: 4 }
    }}>
      
      {/* Header */}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between', 
        alignItems: { xs: 'stretch', sm: 'center' },
        gap: 2,
        mb: 4
      }}>
        <Box>
          <Typography 
            variant={isMobile ? "h5" : "h4"} 
            component="h1"
            sx={{ 
              fontWeight: 'bold',
              color: '#1A237E',
              mb: 0.5
            }}
          >
            👥 Gestión de Trabajadores
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Administra el equipo de trabajo de tu empresa
          </Typography>
        </Box>

        {!isMobile && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/home/trabajadores/crear')}
            sx={{
              background: 'linear-gradient(135deg, #1A237E 0%, #3949AB 100%)',
              borderRadius: 2,
              px: 3,
              py: 1.5,
              textTransform: 'none',
              fontWeight: 'bold',
              boxShadow: '0 4px 15px rgba(26, 35, 126, 0.3)',
              '&:hover': {
                background: 'linear-gradient(135deg, #0D47A1 0%, #1976D2 100%)',
                boxShadow: '0 6px 20px rgba(26, 35, 126, 0.4)',
                transform: 'translateY(-2px)'
              },
              transition: 'all 0.3s ease'
            }}
          >
            Nuevo Trabajador
          </Button>
        )}
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {/* Estadísticas */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <Card sx={{ borderRadius: 2, textAlign: 'center', p: 2 }}>
            <Typography variant="h4" sx={{ color: '#1A237E', fontWeight: 'bold' }}>
              {estadisticas.total}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total Trabajadores
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card sx={{ borderRadius: 2, textAlign: 'center', p: 2 }}>
            <Typography variant="h4" sx={{ color: '#4CAF50', fontWeight: 'bold' }}>
              {estadisticas.activos}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Activos
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card sx={{ borderRadius: 2, textAlign: 'center', p: 2 }}>
            <Typography variant="h4" sx={{ color: '#FF9800', fontWeight: 'bold' }}>
              {estadisticas.supervisores}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Supervisores
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card sx={{ borderRadius: 2, textAlign: 'center', p: 2 }}>
            <Typography variant="h4" sx={{ color: '#2196F3', fontWeight: 'bold' }}>
              {estadisticas.operarios}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Operarios
            </Typography>
          </Card>
        </Grid>
      </Grid>

      {/* Barra de búsqueda */}
      <Box sx={{ mb: 3 }}>
        <TextField
          variant="outlined"
          fullWidth={isMobile}
          size="small"
          placeholder="Buscar por nombre, email, documento..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: '#4FC3F7' }} />
              </InputAdornment>
            ),
          }}
          sx={{ 
            width: isMobile ? '100%' : 400,
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              '&:hover fieldset': {
                borderColor: '#4FC3F7',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#1A237E',
              },
            }
          }}
        />
      </Box>

      {/* Vista móvil - Cards */}
      {isMobile ? (
        <Box>
          {filteredTrabajadores
            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
            .map((trabajador) => (
              <TrabajadorCard
                key={trabajador.id_trabajador}
                trabajador={trabajador}
                onView={handleView}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onResend={handleReenviarBienvenida}
              />
            ))}
          
          {filteredTrabajadores.length > rowsPerPage && (
            <Paper sx={{ mt: 2, borderRadius: 2 }}>
              <TablePagination
                rowsPerPageOptions={[20]}
                component="div"
                count={filteredTrabajadores.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                labelDisplayedRows={({ from, to, count }) =>
                  `${from}-${to} de ${count}`
                }
                labelRowsPerPage="Filas por página:"
              />
            </Paper>
          )}
        </Box>
      ) : (
        /* Vista desktop - Tabla mejorada */
        <Paper sx={{ 
          borderRadius: 3,
          overflow: 'hidden',
          border: '1px solid rgba(26, 35, 126, 0.1)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
        }}>
          <TableContainer>
            <Table>
              <TableHead sx={{ 
                background: 'linear-gradient(135deg, #1A237E 0%, #3949AB 100%)' 
              }}>
                <TableRow>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                    Trabajador
                  </TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                    <TableSortLabel
                      active={orderBy === 'nombre_trabajador'}
                      direction={orderBy === 'nombre_trabajador' ? order : 'asc'}
                      onClick={createSortHandler('nombre_trabajador')}
                      sx={{ 
                        color: 'white !important',
                        '& .MuiTableSortLabel-icon': { color: 'white !important' }
                      }}
                    >
                      Nombre
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                    Documento
                  </TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                    <TableSortLabel
                      active={orderBy === 'email_trabajador'}
                      direction={orderBy === 'email_trabajador' ? order : 'asc'}
                      onClick={createSortHandler('email_trabajador')}
                      sx={{ 
                        color: 'white !important',
                        '& .MuiTableSortLabel-icon': { color: 'white !important' }
                      }}
                    >
                      Email
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                    Teléfono
                  </TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                    Nivel
                  </TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                    Acciones
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredTrabajadores
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((trabajador, index) => (
                    <TableRow 
                      key={trabajador.id_trabajador}
                      sx={{ 
                        '&:nth-of-type(even)': { 
                          backgroundColor: 'rgba(26, 35, 126, 0.02)' 
                        },
                        '&:hover': {
                          backgroundColor: 'rgba(79, 195, 247, 0.1)',
                          transform: 'scale(1.01)',
                          transition: 'all 0.2s ease'
                        }
                      }}
                    >
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#1A237E' }}>
                          {trabajador.id_trabajador}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar
                            sx={{
                              width: 40,
                              height: 40,
                              background: 'linear-gradient(135deg, #4FC3F7 0%, #29B6F6 100%)',
                              color: 'white',
                              fontSize: '0.9rem',
                              fontWeight: 'bold'
                            }}
                          >
                            {getInitials(trabajador.nombre_trabajador, trabajador.apellido_trabajador)}
                          </Avatar>
                          <Box>
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                              {trabajador.nombre_trabajador} {trabajador.apellido_trabajador}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {trabajador.profesion || 'Trabajador'}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {trabajador.numero_documento || '-'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {trabajador.tipo_documento || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                          {trabajador.email_trabajador || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {trabajador.telefono_trabajador || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          <Chip 
                            label={trabajador.descripcion_escala || '-'}
                            size="small"
                            sx={{ 
                              bgcolor: 'rgba(255, 152, 0, 0.1)',
                              color: '#FF9800',
                              fontWeight: 'bold'
                            }}
                          />
                          <Chip 
                            label={trabajador.det_nivel_usuario || '-'}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Tooltip title="Ver detalles">
                            <IconButton
                              size="small"
                              onClick={() => handleView(trabajador)}
                              sx={{ 
                                bgcolor: 'info.light', 
                                color: 'white',
                                '&:hover': { bgcolor: 'info.main' }
                              }}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Editar trabajador">
                            <IconButton
                              size="small"
                              onClick={() => handleEdit(trabajador)}
                              sx={{ 
                                bgcolor: 'primary.light', 
                                color: 'white',
                                '&:hover': { bgcolor: 'primary.main' }
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Enviar onboarding APP">
                            <IconButton
                              size="small"
                              onClick={() => handleReenviarBienvenida(trabajador)}
                              disabled={!trabajador.email_trabajador}
                              sx={{
                                bgcolor: '#25D366',
                                color: 'white',
                                '&:hover': { bgcolor: '#128C7E' },
                                '&.Mui-disabled': { bgcolor: '#E2E8F0' }
                              }}
                            >
                              <SendIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Eliminar trabajador">
                            <IconButton
                              size="small"
                              onClick={() => handleDelete(trabajador)}
                              sx={{
                                bgcolor: 'error.light',
                                color: 'white',
                                '&:hover': { bgcolor: 'error.main' }
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          
          <TablePagination
            rowsPerPageOptions={[20]}
            component="div"
            count={filteredTrabajadores.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            labelDisplayedRows={({ from, to, count }) =>
              `${from}-${to} de ${count}`
            }
            labelRowsPerPage="Filas por página:"
            sx={{
              borderTop: '1px solid rgba(26, 35, 126, 0.1)',
              backgroundColor: 'rgba(26, 35, 126, 0.02)'
            }}
          />
        </Paper>
      )}

      {/* FAB para móvil */}
      {isMobile && (
        <Fab
          color="primary"
          onClick={() => navigate('/home/trabajadores/crear')}
          sx={{
            position: 'fixed',
            bottom: 20,
            right: 20,
            background: 'linear-gradient(135deg, #1A237E 0%, #3949AB 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #0D47A1 0%, #1976D2 100%)',
            }
          }}
        >
          <AddIcon />
        </Fab>
      )}

      {/* Dialog de detalles */}
      <DetalleTrabjadorDialog 
        open={openDetail}
        onClose={() => setOpenDetail(false)}
        trabajador={selectedTrabajador}
      />

      {/* Mensaje cuando no hay trabajadores */}
      {filteredTrabajadores.length === 0 && !loading && (
        <Paper sx={{ 
          p: 4, 
          textAlign: 'center', 
          borderRadius: 3,
          border: '2px dashed rgba(26, 35, 126, 0.2)'
        }}>
          <GroupIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {searchTerm ? 'No se encontraron trabajadores' : 'No hay trabajadores registrados'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {searchTerm 
              ? 'Prueba con otros términos de búsqueda'
              : 'Comienza agregando trabajadores a tu equipo'
            }
          </Typography>
          {!searchTerm && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate('/home/trabajadores/crear')}
              sx={{
                background: 'linear-gradient(135deg, #1A237E 0%, #3949AB 100%)',
                borderRadius: 2
              }}
            >
              Agregar Primer Trabajador
            </Button>
          )}
        </Paper>
      )}
      {/* Snackbar */}
      {snackbar.open && (
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          sx={{ position: 'fixed', bottom: 20, left: '50%', transform: 'translateX(-50%)',
            zIndex: 9999, borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.15)', minWidth: 300 }}
        >
          {snackbar.msg}
        </Alert>
      )}
    </Box>
  );
}

export default TrabajadoresAdmin;